import { apiRequest, getAuthToken, type components } from './core';
import { getSubdomain } from '@/utils/environment';

// ============================================
// Behavior Categories API (Phase 4)
// ============================================

export type BehaviorCategoryCreateData = components['schemas']['CreateBehaviorCategoryRequest'];
export type BehaviorCategoryUpdateData = components['schemas']['UpdateBehaviorCategoryRequest'];

// Use OpenAPI-generated type (matches backend behaviorCategoryResponseSchema)
export type BehaviorCategory = components['schemas']['BehaviorCategoryResponse'];

export const behaviorCategoriesApi = {
  getAll: async () => apiRequest<{ success: boolean; data: BehaviorCategory[] }>('/api/behavior-categories'),

  /** Get all behavior categories including inactive (admin only) */
  getAllAdmin: async () => apiRequest<{ success: boolean; data: BehaviorCategory[] }>('/api/behavior-categories/all'),

  getById: async (id: string) =>
    apiRequest<{ success: boolean; data: BehaviorCategory }>(`/api/behavior-categories/${id}`),

  create: async (data: BehaviorCategoryCreateData) =>
    apiRequest<{ success: boolean; data: BehaviorCategory }>('/api/behavior-categories', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: async (id: string, data: BehaviorCategoryUpdateData) =>
    apiRequest<{ success: boolean; data: BehaviorCategory }>(`/api/behavior-categories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  /**
   * Deactivate behavior category (soft delete - sets active to false)
   * Note: This is the actual implementation - `delete` is an alias for CrudPage compatibility
   */
  deactivate: async (id: string) =>
    apiRequest<{ success: boolean; message: string }>(`/api/behavior-categories/${id}/deactivate`, {
      method: 'PATCH',
    }),

  /**
   * Alias for deactivate (CrudPage compatibility)
   * Behavior categories are never hard-deleted, only deactivated
   */
  delete: async (id: string) =>
    apiRequest<{ success: boolean; message: string }>(`/api/behavior-categories/${id}/deactivate`, {
      method: 'PATCH',
    }),

  /**
   * Reactivate behavior category (sets active to true)
   */
  reactivate: async (id: string) =>
    apiRequest<{ success: boolean; message: string }>(`/api/behavior-categories/${id}/reactivate`, {
      method: 'PATCH',
    }),

  /**
   * Check behavior category dependencies for smart action dialog
   * Returns what actions are possible and why
   */
  checkDependencies: async (id: string) =>
    apiRequest<{
      success: boolean;
      data: {
        canDeactivate: boolean;
        canDelete: boolean;
        dependencies: {
          activeBehaviorRecords: number;
        };
        blockedBy: string[];
      };
    }>(`/api/behavior-categories/${id}/dependencies`, {
      method: 'GET',
    }),

  swapOrder: async (categoryId1: string, categoryId2: string) =>
    apiRequest<{ success: boolean; data: { category1: BehaviorCategory; category2: BehaviorCategory } }>(
      '/api/behavior-categories/swap-order',
      {
        method: 'POST',
        body: JSON.stringify({ categoryId1, categoryId2 }),
      }
    ),

  /**
   * Get preview of records that would be affected by bulk update
   * Returns total count and count of overridden records
   */
  getBulkUpdatePreview: async (id: string) =>
    apiRequest<{
      success: boolean;
      data: {
        total: number;
        overridden: number;
      };
    }>(`/api/behavior-categories/${id}/bulk-update-preview`),

  /**
   * Bulk update behavior records for a category
   * Updates is_positive and/or points on existing records
   */
  bulkUpdateRecords: async (
    id: string,
    data: {
      updates: { isPositive?: boolean; points?: number };
      includeOverridden?: boolean;
    }
  ) =>
    apiRequest<{
      success: boolean;
      data: { updatedCount: number };
    }>(`/api/behavior-categories/${id}/bulk-update`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
};

// ============================================
// Location Types API (Phase 4)
// ============================================

// Use OpenAPI-generated type (matches backend locationBehaviorTypeResponseSchema)
export type LocationType = components['schemas']['LocationBehaviorTypeResponse'];
export type LocationTypeCreateData = components['schemas']['CreateLocationTypeRequest'];
export type LocationTypeUpdateData = components['schemas']['UpdateLocationTypeRequest'];

export const locationTypesApi = {
  /** Get active location types for behavior logging */
  getAll: async () => apiRequest<{ success: boolean; data: LocationType[] }>('/api/location-types'),

  /** Get all location types including inactive (admin only) */
  getAllAdmin: async () => apiRequest<{ success: boolean; data: LocationType[] }>('/api/location-types/all'),

  getById: async (id: string) =>
    apiRequest<{ success: boolean; data: LocationType }>(`/api/location-types/${id}`),

  create: async (data: LocationTypeCreateData) =>
    apiRequest<{ success: boolean; data: LocationType }>('/api/location-types', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: async (id: string, data: LocationTypeUpdateData) =>
    apiRequest<{ success: boolean; data: LocationType }>(`/api/location-types/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  /**
   * Deactivate location type (soft delete - sets active to false)
   * Note: This is the actual implementation - `delete` is an alias for CrudPage compatibility
   */
  deactivate: async (id: string) =>
    apiRequest<{ success: boolean; message: string }>(`/api/location-types/${id}/deactivate`, {
      method: 'PATCH',
    }),

  /**
   * Alias for deactivate (CrudPage compatibility)
   * Location types are never hard-deleted, only deactivated
   */
  delete: async (id: string) =>
    apiRequest<{ success: boolean; message: string }>(`/api/location-types/${id}/deactivate`, {
      method: 'PATCH',
    }),

  /**
   * Reactivate location type (sets active to true)
   */
  reactivate: async (id: string) =>
    apiRequest<{ success: boolean; message: string }>(`/api/location-types/${id}/reactivate`, {
      method: 'PATCH',
    }),

  /**
   * Check location type dependencies for smart action dialog
   * Returns what actions are possible and why
   */
  checkDependencies: async (id: string) =>
    apiRequest<{
      success: boolean;
      data: {
        canDeactivate: boolean;
        canDelete: boolean;
        dependencies: {
          activeBehaviorRecords: number;
        };
        blockedBy: string[];
      };
    }>(`/api/location-types/${id}/dependencies`, {
      method: 'GET',
    }),

  swapOrder: async (locationTypeId1: string, locationTypeId2: string) =>
    apiRequest<{ success: boolean; data: { locationType1: LocationType; locationType2: LocationType } }>(
      '/api/location-types/swap-order',
      {
        method: 'POST',
        body: JSON.stringify({ locationTypeId1, locationTypeId2 }),
      }
    ),
};

// ============================================
// Behavior Records API (Phase 5)
// ============================================

export type BehaviorCreateData = components['schemas']['CreateBehaviorRequest'];
export type BehaviorUpdateData = components['schemas']['UpdateBehaviorRequest'];
export type BulkBehaviorCreateData = components['schemas']['BulkCreateBehaviorRequest'];

// Response type from auto-generated OpenAPI schemas
export type BehaviorRecord = components['schemas']['BehaviorResponse'];

// Staff behavior entry type (enriched with student/category/location data)
export type StaffBehaviorEntry = components['schemas']['StaffBehaviorEntryResponse'];

export const behaviorApi = {
  getAll: async (filters?: { studentId?: string; blockId?: string; startDate?: string; endDate?: string }) => {
    const params = new URLSearchParams();
    if (filters?.studentId) params.append('studentId', filters.studentId);
    if (filters?.blockId) params.append('blockId', filters.blockId);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);

    const url = params.toString() ? `/api/behavior?${params.toString()}` : '/api/behavior';

    return apiRequest<{ success: boolean; data: BehaviorRecord[] }>(url);
  },

  /**
   * Get behavior entries logged by a specific staff member (admin only)
   * @param staffId - Staff member UUID
   * @param options - Query options (limit, startDate, endDate)
   */
  getByStaff: async (
    staffId: string,
    options?: { limit?: number; startDate?: string; endDate?: string }
  ) => {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.startDate) params.append('startDate', options.startDate);
    if (options?.endDate) params.append('endDate', options.endDate);

    const url = params.toString()
      ? `/api/behavior/staff/${staffId}?${params.toString()}`
      : `/api/behavior/staff/${staffId}`;

    return apiRequest<{ success: boolean; data: StaffBehaviorEntry[] }>(url);
  },

  getById: async (id: string) => apiRequest<{ success: boolean; data: BehaviorRecord }>(`/api/behavior/${id}`),

  getTodayCount: async (staffId?: string) => {
    const url = staffId ? `/api/behavior/today/count?staffId=${staffId}` : '/api/behavior/today/count';
    return apiRequest<{ success: boolean; data: { count: number } }>(url);
  },

  create: async (data: BehaviorCreateData) =>
    apiRequest<{ success: boolean; data: BehaviorRecord }>('/api/behavior', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  bulkCreate: async (data: BulkBehaviorCreateData) =>
    apiRequest<{
      success: boolean;
      successful: number;
      failed: number;
      bulkGroupId: string;
      data: BehaviorRecord[];
      errors: Array<{ studentId: string; reason: string }>;
    }>('/api/behavior/bulk', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Bulk group operations (edit/delete all records in a bulk group)
  getBulkGroup: async (bulkGroupId: string) =>
    apiRequest<{
      success: boolean;
      data: Array<BehaviorRecord & {
        studentFirstName: string;
        studentLastName: string;
        categoryName: string;
        categoryColor: string;
        blockName: string | null;
        locationName: string | null;
      }>;
      count: number;
    }>(`/api/behavior/bulk-group/${bulkGroupId}`),

  bulkUpdate: async (bulkGroupId: string, data: BehaviorUpdateData) =>
    apiRequest<{
      success: boolean;
      updated: number;
      skipped: number;
      data: BehaviorRecord[];
      errors: Array<{ studentId: string; studentName: string; reason: string }>;
    }>(`/api/behavior/bulk-group/${bulkGroupId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  bulkDelete: async (bulkGroupId: string, deletionNote?: string) =>
    apiRequest<{
      success: boolean;
      deleted: number;
      skipped: number;
      message: string;
      errors: Array<{ studentId: string; studentName: string; reason: string }>;
    }>(`/api/behavior/bulk-group/${bulkGroupId}`, {
      method: 'DELETE',
      body: deletionNote ? JSON.stringify({ deletionNote }) : undefined,
    }),

  update: async (id: string, data: BehaviorUpdateData) =>
    apiRequest<{ success: boolean; data: BehaviorRecord }>(`/api/behavior/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: async (id: string, deletionNote?: string) =>
    apiRequest<{ success: boolean; message: string }>(`/api/behavior/${id}`, {
      method: 'DELETE',
      body: deletionNote ? JSON.stringify({ deletionNote }) : undefined,
    }),

  /**
   * Upload photo evidence for a behavior record
   */
  uploadEvidence: async (behaviorRecordId: string, file: File) => {
    const formData = new FormData();
    formData.append('evidence', file);

    // Get auth token for the Authorization header
    const accessToken = await getAuthToken();
    const headers: HeadersInit = {};

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    // Add tenant subdomain header for multi-tenant routing
    const subdomain = getSubdomain();
    if (subdomain) {
      headers['X-Tenant-Subdomain'] = subdomain;
    }

    const response = await fetch(`/api/files/behavior/${behaviorRecordId}/evidence`, {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(errorData.error || `Upload failed with status ${response.status}`);
    }

    return response.json();
  },

  /**
   * Delete photo evidence from a behavior record
   */
  deleteEvidence: async (behaviorRecordId: string, fileId: string) =>
    apiRequest<{ success: boolean; message: string }>(`/api/files/behavior/${behaviorRecordId}/evidence/${fileId}`, {
      method: 'DELETE',
    }),
};
